import React, { ClipboardEvent, useRef, useState } from "react";

import CustomError from "src/app/libs/customError";
import ValidateFiles from "src/app/libs/validateFiles";
import { handleError } from "src/utils/error";
import {
  ALLOWED_EXTENSIONS,
  MAX_FILE_SIZE_MB,
  MAX_FILES,
} from "../../utils/fileUpload";

export function useFileAttachments() {
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (picked: File[]) => {
    if (!picked.length) return;

    const accepted: File[] = [];
    let remainingSlots = MAX_FILES - files.length;

    picked.forEach((file) => {
      try {
        new ValidateFiles({
          files: [file],
          allowedTypes: ALLOWED_EXTENSIONS,
          maxSize: MAX_FILE_SIZE_MB,
        }).validate();

        if (remainingSlots <= 0) {
          throw new CustomError({
            id: "ws_ai_chat_file_limit",
            defaultMessage: "You can attach up to 10 files.",
          });
        }

        accepted.push(file);
        remainingSlots -= 1;
      } catch (error) {
        handleError(error);
      }
    });

    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted]);
    }
  };

  const handleFileClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    addFiles(picked);
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = Array.from(e.clipboardData.files ?? []);
    if (!pasted.length) return;
    e.preventDefault();
    e.stopPropagation();
    addFiles(pasted);
  };

  const handleRemoveFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const clearFiles = () => setFiles([]);

  const restoreFiles = (f: File[]) => setFiles(f);

  return {
    files,
    fileInputRef,
    handleFileClick,
    handleFileChange,
    handlePaste,
    handleRemoveFile,
    clearFiles,
    restoreFiles,
  };
}
